import { useEffect, useCallback } from 'react';
import { Uppy, type UppyFile, type UppyOptions } from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import type { UseUppyUploaderConfig, UseUppyUploaderReturn } from '#types/uppy-uploader';

/**
 * Stores Uppy instances keyed by queue name at module level.
 * Instances persist for the entire app session (until page refresh).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Map stores instances with different generic types
const instancesMap = new Map<string, Uppy<any, any>>();

/**
 * Get or create a persistent Uppy instance.
 * @internal
 */
function getOrCreateInstance<
  TMeta extends Record<string, unknown> = Record<string, unknown>,
  TBody extends Record<string, unknown> = Record<string, unknown>,
>(queueName: string, options?: Partial<UppyOptions<TMeta, TBody>>): Uppy<TMeta, TBody> {
  if (!instancesMap.has(queueName)) {
    const instance = new Uppy<TMeta, TBody>({
      id: queueName,
      ...options,
    });
    instancesMap.set(queueName, instance);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return -- guaranteed to exist since we create it above if missing
  return instancesMap.get(queueName) as any;
}

/**
 * Hook for file uploads with XHR configuration and persistent instances.
 *
 * @param queueName - Unique identifier for this upload queue
 * @param config - Upload configuration
 * @returns Upload utilities
 */
export function useUppyUploader<
  TMeta extends Record<string, unknown> = Record<string, unknown>,
  TBody extends Record<string, unknown> = Record<string, unknown>,
>(
  queueName: string,
  config: UseUppyUploaderConfig<TMeta, TBody>
): UseUppyUploaderReturn<TMeta, TBody> {
  const {
    endpoint,
    fieldName,
    headers,
    uppyOptions = {},
    xhrOptions = {},
    onUploadProgress,
    onUploadSuccess,
    onUploadError,
    onRestrictionFailed,
  } = config;

  const uppy = getOrCreateInstance<TMeta, TBody>(queueName, uppyOptions);

  useEffect(() => {
    const hasXHRUpload = uppy.getPlugin('XHRUpload');

    if (!hasXHRUpload) {
      uppy.use(XHRUpload<TMeta, TBody>, {
        endpoint,
        fieldName,
        formData: true,
        withCredentials: true,
        headers,
        ...xhrOptions,
      });
    }
  }, [uppy, endpoint, fieldName, headers, xhrOptions]);

  useEffect(() => {
    if (onUploadProgress) {
      const handleUploadProgress = (file?: UppyFile<TMeta, TBody>) => {
        onUploadProgress(file);
      };

      uppy.on('upload-progress', handleUploadProgress);

      return () => {
        uppy.off('upload-progress', handleUploadProgress);
      };
    }

    return () => null;
  }, [uppy, onUploadProgress]);

  useEffect(() => {
    if (onUploadSuccess) {
      const handleUploadSuccess = (
        file?: UppyFile<TMeta, TBody>,
        response?: { body?: TBody; status?: number; uploadURL?: string }
      ) => {
        onUploadSuccess(file, response ?? {});
      };

      uppy.on('upload-success', handleUploadSuccess);

      return () => {
        uppy.off('upload-success', handleUploadSuccess);
      };
    }

    return () => null;
  }, [uppy, onUploadSuccess]);

  useEffect(() => {
    if (onUploadError) {
      const handleUploadError = (
        file?: UppyFile<TMeta, TBody>,
        error?: unknown,
        response?: { body?: TBody; status?: number }
      ) => {
        onUploadError(file, error, response ?? {});
      };

      uppy.on('upload-error', handleUploadError);

      return () => {
        uppy.off('upload-error', handleUploadError);
      };
    }

    return () => null;
  }, [uppy, onUploadError]);

  useEffect(() => {
    if (onRestrictionFailed) {
      const handleRestrictionFailed = (file?: UppyFile<TMeta, TBody>, error?: Error) => {
        onRestrictionFailed(file, error);
      };

      uppy.on('restriction-failed', handleRestrictionFailed);

      return () => {
        uppy.off('restriction-failed', handleRestrictionFailed);
      };
    }

    return () => null;
  }, [uppy, onRestrictionFailed]);

  const addFiles = useCallback(
    (files: File[]) => {
      files.forEach(file => {
        try {
          uppy.addFile({
            data: file,
            name: file.name,
            type: file.type,
          });
        } catch {
          // Silently ignore file add errors - Uppy will handle validation errors through events
        }
      });
      void uppy.upload();
    },
    [uppy]
  );

  const getFiles = useCallback(() => {
    return uppy.getFiles();
  }, [uppy]);

  const clearFiles = useCallback(() => {
    uppy.cancelAll();
    const files = uppy.getFiles();
    files.forEach(file => {
      uppy.removeFile(file.id);
    });
  }, [uppy]);

  const removeFile = useCallback(
    (fileId: string) => {
      uppy.removeFile(fileId);
    },
    [uppy]
  );

  return {
    addFiles,
    getFiles,
    clearFiles,
    removeFile,
  };
}
